import { FechaCR } from '@/components/dominio';
import { ModalCrearEnsayoEsclerometro } from '@/components/dominio/ModalCrearEnsayoEsclerometro';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useEnsayosEsclerometro } from '@/hooks/useEsclerometro';
import { puedeEscribirLab } from '@/lib/cliente';
import { ChevronLeft, ChevronRight, Plus, Search } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

/**
 * `/lab/esclerometro` — listado de ensayos no destructivos (martillo Schmidt).
 *
 * Filtros (persistidos en URL):
 *   - obra_works_no
 *   - desde/hasta (rango de fecha del ensayo)
 *   - q (búsqueda libre en elemento estructural / id_casa / equipo)
 *
 * Click en fila → /lab/esclerometro/:id.
 */

const POR_PAGINA = 25;
const SKELETON_FILAS = ['s-a', 's-b', 's-c', 's-d', 's-e'] as const;
const SKELETON_COLS = ['c0', 'c1', 'c2', 'c3', 'c4', 'c5', 'c6', 'c7'] as const;

export function EsclerometroListaPantalla() {
  const navegar = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const setParams = useCallback(
    (changes: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(changes)) {
            if (v === null || v === '') next.delete(k);
            else next.set(k, v);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const obraWorksNo = searchParams.get('obra_works_no') ?? '';
  const desde = searchParams.get('desde') ?? '';
  const hasta = searchParams.get('hasta') ?? '';
  const q = searchParams.get('q') ?? '';
  const paginaRaw = searchParams.get('pagina');
  const pagina = paginaRaw !== null && /^\d+$/.test(paginaRaw) ? Math.max(1, Number(paginaRaw)) : 1;

  const [modalNuevo, setModalNuevo] = useState(false);
  const puedeEscribir = puedeEscribirLab();

  const filtros = useMemo(
    () => ({
      obra_works_no: obraWorksNo.trim() || undefined,
      desde: desde || undefined,
      hasta: hasta || undefined,
      q: q.trim() || undefined,
      pagina,
      por_pagina: POR_PAGINA,
    }),
    [obraWorksNo, desde, hasta, q, pagina],
  );

  const consulta = useEnsayosEsclerometro(filtros);
  const data = consulta.data?.data;
  const ensayos = data?.ensayos ?? [];
  const total = data?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(total / POR_PAGINA));

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Laboratorio — Esclerómetro</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Ensayo no destructivo (martillo Schmidt). Cada ensayo registra N golpes sobre un
            elemento estructural; el promedio descarta máximo y mínimo cuando hay 3 o más.
          </p>
        </div>
        {puedeEscribir && (
          <Button onClick={() => setModalNuevo(true)} className="gap-1.5">
            <Plus className="h-4 w-4" /> Nuevo ensayo
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Filtros</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="grid w-full gap-1.5 sm:w-auto">
            <Label htmlFor="f-obra">Obra (works_no)</Label>
            <Input
              id="f-obra"
              value={obraWorksNo}
              onChange={(e) => setParams({ obra_works_no: e.target.value || null, pagina: null })}
              placeholder="VB, 6.24, Casa…"
              className="w-full font-mono sm:w-[160px]"
            />
          </div>

          <div className="grid w-full gap-1.5 sm:w-auto">
            <Label htmlFor="f-desde">Desde</Label>
            <Input
              id="f-desde"
              type="date"
              value={desde}
              onChange={(e) => setParams({ desde: e.target.value || null, pagina: null })}
              className="w-full sm:w-[160px]"
            />
          </div>

          <div className="grid w-full gap-1.5 sm:w-auto">
            <Label htmlFor="f-hasta">Hasta</Label>
            <Input
              id="f-hasta"
              type="date"
              value={hasta}
              onChange={(e) => setParams({ hasta: e.target.value || null, pagina: null })}
              className="w-full sm:w-[160px]"
            />
          </div>

          <div className="grid flex-1 min-w-[200px] gap-1.5">
            <Label htmlFor="f-q">Buscar</Label>
            <div className="relative">
              <Search
                className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                id="f-q"
                value={q}
                onChange={(e) => setParams({ q: e.target.value || null, pagina: null })}
                placeholder="Elemento, casa, equipo…"
                className="pl-8"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {/* Móvil */}
          <div className="space-y-2 p-2 md:hidden">
            {consulta.isLoading &&
              ['s-a', 's-b', 's-c'].map((k) => (
                <div key={k} className="h-24 animate-pulse rounded-md border bg-muted" />
              ))}
            {!consulta.isLoading && ensayos.length === 0 && (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No hay ensayos que coincidan con los filtros.
              </div>
            )}
            {!consulta.isLoading &&
              ensayos.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() =>
                    navegar(`/lab/esclerometro/${e.id}`, {
                      state: { from: `${location.pathname}${location.search}` },
                    })
                  }
                  className="flex w-full flex-col gap-1 rounded-md border bg-white p-3 text-left transition hover:bg-zinc-50 active:bg-zinc-100"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-base font-semibold">#{e.numero}</span>
                    <FechaCR fecha={e.fecha} formato="corto" />
                  </div>
                  <div className="text-sm font-medium">{e.elemento_estructural}</div>
                  {(e.obra_works_no || e.id_casa) && (
                    <div className="text-xs text-muted-foreground">
                      {e.obra_works_no && <span className="font-mono">{e.obra_works_no}</span>}
                      {e.obra_display_name && <span> — {e.obra_display_name}</span>}
                      {e.id_casa && <span> · {e.id_casa}</span>}
                    </div>
                  )}
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs">
                    <span>
                      {e.cantidad_rebotes} golpe{e.cantidad_rebotes === 1 ? '' : 's'}
                    </span>
                    {e.rebote_promedio !== null && (
                      <>
                        <span className="text-muted-foreground">·</span>
                        <span className="font-mono font-medium">
                          R̄ {e.rebote_promedio.toFixed(1)}
                        </span>
                      </>
                    )}
                    {e.edad_dias !== null && (
                      <>
                        <span className="text-muted-foreground">·</span>
                        <span>{e.edad_dias}d</span>
                      </>
                    )}
                  </div>
                </button>
              ))}
          </div>

          {/* Desktop */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">#</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Obra</TableHead>
                  <TableHead>Casa / ubic.</TableHead>
                  <TableHead>Elemento estructural</TableHead>
                  <TableHead className="text-right">Edad (d)</TableHead>
                  <TableHead className="text-right">Golpes</TableHead>
                  <TableHead className="text-right">R̄ promedio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {consulta.isLoading &&
                  SKELETON_FILAS.map((idFila) => (
                    <TableRow key={idFila}>
                      {SKELETON_COLS.map((idCol) => (
                        <TableCell key={`${idFila}-${idCol}`}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                {!consulta.isLoading && ensayos.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      No hay ensayos que coincidan con los filtros.
                    </TableCell>
                  </TableRow>
                )}
                {!consulta.isLoading &&
                  ensayos.map((e) => (
                    <TableRow
                      key={e.id}
                      onClick={() =>
                        navegar(`/lab/esclerometro/${e.id}`, {
                          state: { from: `${location.pathname}${location.search}` },
                        })
                      }
                      className="cursor-pointer hover:bg-zinc-50"
                    >
                      <TableCell className="font-mono font-medium">{e.numero}</TableCell>
                      <TableCell className="font-mono text-sm">
                        <FechaCR fecha={e.fecha} formato="corto" />
                      </TableCell>
                      <TableCell className="text-sm">
                        {e.obra_works_no ? (
                          <div title={e.obra_display_name ?? e.obra_works_no}>
                            <div className="font-mono text-xs">{e.obra_works_no}</div>
                            {e.obra_display_name && (
                              <div className="truncate text-xs text-muted-foreground max-w-[180px]">
                                {e.obra_display_name}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="italic text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {e.id_casa || <span className="italic text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-sm">{e.elemento_estructural}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        {e.edad_dias ?? <span className="italic text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        {e.cantidad_rebotes}
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        {e.rebote_promedio !== null ? (
                          e.rebote_promedio.toFixed(1)
                        ) : (
                          <span className="italic text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>

          {!consulta.isLoading && total > 0 && (
            <div className="flex flex-col gap-2 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-muted-foreground">
                {ensayos.length === 0
                  ? '0 resultados'
                  : `Mostrando ${(pagina - 1) * POR_PAGINA + 1}–${
                      (pagina - 1) * POR_PAGINA + ensayos.length
                    } de ${total}`}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagina <= 1}
                  onClick={() =>
                    setParams({
                      pagina: pagina - 1 <= 1 ? null : String(pagina - 1),
                    })
                  }
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>
                <span className="text-sm tabular-nums">
                  {pagina} / {totalPaginas}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagina >= totalPaginas}
                  onClick={() => setParams({ pagina: String(pagina + 1) })}
                >
                  Siguiente
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {consulta.error && (
        <p className="text-sm text-destructive">
          Error al cargar ensayos: {consulta.error.message}
        </p>
      )}

      <ModalCrearEnsayoEsclerometro
        abierto={modalNuevo}
        cerrar={() => setModalNuevo(false)}
        onCreado={(id) =>
          navegar(`/lab/esclerometro/${id}`, {
            state: { from: `${location.pathname}${location.search}` },
          })
        }
      />
    </div>
  );
}
